// App.jsx
import React, { useState, useEffect } from 'react';
import {
  Calendar, MapPin, Users, Plus, Edit3, Trash2, User,
  Mail, Building, Check, X, Settings, Activity,
  TrendingUp, RefreshCw, LogOut, AlertCircle
} from 'lucide-react';

// ==== Context (DEVE esistere in ./contexts/AuthContext) ====
import { AuthProvider, useAuth } from './contexts/AuthContext';

// ==== Componenti modulari (già nei tuoi file) ====
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import ForgotPasswordForm from './components/auth/ForgotPasswordForm';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import ProfileView from './components/profile/ProfileView';

// ==== Config API ====
const API_BASE = import.meta.env.VITE_API_BASE || '/api';


/* ==========================
   Header autenticato
   ========================== */
const AuthenticatedHeader = ({ currentView, setCurrentView }) => {
  const { user, logout, isAdmin, isCollaborator } = useAuth();

  const roleLabel = (r) => r === 'admin' ? 'Amministratore' : r === 'collaborator' ? 'Collaboratore' : 'Ospite';
  const roleBadge = (r) =>
    r === 'admin' ? 'bg-red-100 text-red-800' :
    r === 'collaborator' ? 'bg-yellow-100 text-yellow-800' :
    'bg-green-100 text-green-800';

  return (
    <header className="bg-white shadow-lg border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <Calendar className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">BookingApp Pro</h1>
              <p className="text-sm text-gray-500">Sistema con Autenticazione</p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm text-green-700 font-medium">Sistema Online</span>
            </div>

            {(isAdmin || isCollaborator) && (
              <nav className="hidden md:flex items-center gap-1">
                {[
                  { id: 'dashboard', label: 'Dashboard', icon: Building },
                  { id: 'events', label: 'Eventi', icon: Calendar },
                  { id: 'bookings', label: 'Prenotazioni', icon: Users },
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setCurrentView(id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                      currentView === id
                        ? 'bg-blue-100 text-blue-700 font-semibold shadow-md'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </nav>
            )}

            {/* Menu utente (semplice, sempre visibile) */}
            <div className="relative">
              <div className="flex items-center gap-3 p-2 rounded-xl">
                <div className="text-right">
                  <div className="font-semibold text-gray-800">{user.name}</div>
                  <div className="text-sm text-gray-500">{user.email}</div>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <div className="w-10 h-10 bg-gradient-to-r from-gray-600 to-gray-800 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleBadge(user.role)}`}>
                    {roleLabel(user.role)}
                  </span>
                </div>
              </div>
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="font-semibold text-gray-800">{user.name}</div>
                  <div className="text-sm text-gray-500">{user.email}</div>
                  {user.company && <div className="text-sm text-gray-500">{user.company}</div>}
                </div>
                <button onClick={() => setCurrentView('profile')} className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                  <Settings className="w-4 h-4" /> Il mio profilo
                </button>
                <button onClick={logout} className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 flex items-center gap-2">
                  <LogOut className="w-4 h-4" /> Esci
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

/* ==========================
   Vista Utente (prenotazioni)
   ========================== */
const UserBookingsView = () => {
  const { user, token } = useAuth();
  const [userBookings, setUserBookings] = useState([]);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(null), 4000); };
  const showError = (msg) => { setError(msg); setTimeout(() => setError(null), 4000); };
  const formatDate = (s) => new Date(s).toLocaleDateString('it-IT');

  useEffect(() => {
    fetchUserBookings();
    fetchAvailableEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserBookings = async () => {
    try {
      setLoading(true);
      const r = await fetch(`${API_BASE}/bookings/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await r.json();
      if (data.success) setUserBookings(data.data);
      else showError('Errore nel caricamento delle prenotazioni');
    } catch {
      showError('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableEvents = async () => {
    try {
      const r = await fetch(`${API_BASE}/events/available`);
      const data = await r.json();
      if (data.success) setAvailableEvents(data.data);
      else showError('Errore nel caricamento degli eventi');
    } catch {
      showError('Errore di connessione al server');
    }
  };

  const bookEvent = async (eventId) => {
    try {
      setLoading(true);
      const r = await fetch(`${API_BASE}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          event: eventId,
          guestName: user.name,
          guestEmail: user.email,
          company: user.company || ''
        })
      });
      const data = await r.json();
      if (data.success) {
        showSuccess('Prenotazione effettuata con successo!');
        fetchUserBookings();
        fetchAvailableEvents();
      } else {
        showError(data.message || 'Errore durante la prenotazione');
      }
    } catch {
      showError('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {success && (
        <div className="fixed top-4 right-4 z-50 p-4 rounded-xl shadow-xl flex items-center gap-3 bg-green-100 text-green-800 border border-green-200">
          <Check className="w-5 h-5" />
          <span className="font-medium">{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-2"><X className="w-4 h-4" /></button>
        </div>
      )}
      {error && (
        <div className="fixed top-4 right-4 z-50 p-4 rounded-xl shadow-xl flex items-center gap-3 bg-red-100 text-red-800 border border-red-200">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">{error}</span>
          <button onClick={() => setError(null)} className="ml-2"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Benvenuto */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-8 rounded-2xl text-white">
        <h2 className="text-3xl font-bold mb-2">Benvenuto, {user.name}!</h2>
        <p className="text-blue-100">Gestisci le tue prenotazioni agli eventi</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Eventi Disponibili */}
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">Eventi Disponibili</h3>

          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full border-2 border-blue-600 border-t-transparent w-8 h-8"></div>
              <span className="ml-4 text-gray-600">Caricamento eventi...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {availableEvents.map(evt => (
                <div key={evt._id} className="border border-gray-200 p-4 rounded-xl hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-800 mb-2">{evt.title}</h4>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(evt.date)} alle {evt.time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{evt.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span>{evt.bookedParticipants || 0}/{evt.maxParticipants} posti</span>
                        </div>
                      </div>
                      {evt.description && <p className="text-gray-600 text-sm mt-2">{evt.description}</p>}

                      <div className="mt-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${Math.min(((evt.bookedParticipants || 0) / evt.maxParticipants) * 100, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{evt.bookedParticipants || 0} posti</span>
                          <span>{evt.maxParticipants} posti</span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => bookEvent(evt._id)}
                      disabled={loading || (evt.bookedParticipants >= evt.maxParticipants)}
                      className="ml-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                    >
                      {(evt.bookedParticipants >= evt.maxParticipants) ? 'Completo' : 'Prenota'}
                    </button>
                  </div>
                </div>
              ))}

              {availableEvents.length === 0 && (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Nessun evento disponibile al momento</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mie Prenotazioni */}
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">Le Mie Prenotazioni</h3>

          <div className="space-y-4">
            {userBookings.map(bk => (
              <div key={bk._id} className="border border-gray-200 p-4 rounded-xl">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-800 mb-2">{bk.event?.title || 'Evento eliminato'}</h4>
                    {bk.event && (
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(bk.event.date)} alle {bk.event.time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{bk.event.location}</span>
                        </div>
                      </div>
                    )}
                    <div className="mt-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        bk.status === 'confirmed' ? 'bg-green-100 text-green-800'
                          : bk.status === 'cancelled' ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {bk.status === 'confirmed' ? 'Confermata' : bk.status === 'cancelled' ? 'Cancellata' : 'In Attesa'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {userBookings.length === 0 && (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Non hai ancora effettuato prenotazioni</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==========================
   Admin Dashboard
   ========================== */
const AdminDashboard = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const showSuccess = (m) => { setSuccess(m); setTimeout(() => setSuccess(null), 4000); };
  const showError = (m) => { setError(m); setTimeout(() => setError(null), 4000); };
  const formatDate = (s) => new Date(s).toLocaleDateString('it-IT');

  const fetchEvents = async () => {
    try {
      const r = await fetch(`${API_BASE}/events`);
      const data = await r.json();
      if (data.success) setEvents(data.data);
      else showError('Errore nel caricamento degli eventi');
    } catch {
      showError('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const r = await fetch(`${API_BASE}/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      if (data.success) setBookings(data.data);
      else showError('Errore nel caricamento delle prenotazioni');
    } catch {
      showError('Errore di connessione al server');
    }
  };

  useEffect(() => { fetchEvents(); fetchBookings(); /* eslint-disable-next-line */ }, []);

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const r = await fetch(`${API_BASE}/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      const data = await r.json();
      if (data.success) {
        showSuccess('Stato prenotazione aggiornato!');
        fetchBookings(); fetchEvents();
      } else showError(data.message || 'Errore nell\'aggiornamento');
    } catch {
      showError('Errore di connessione');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa prenotazione?')) return;
    try {
      const r = await fetch(`${API_BASE}/bookings/${bookingId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await r.json();
      if (data.success) {
        showSuccess('Prenotazione eliminata con successo!');
        fetchBookings(); fetchEvents();
      } else showError(data.message || 'Errore nell\'eliminazione');
    } catch {
      showError('Errore di connessione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full border-4 border-blue-600 border-t-transparent w-12 h-12"></div>
        <span className="ml-4 text-gray-600">Caricamento dati...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {success && (
        <div className="fixed top-4 right-4 z-50 p-4 rounded-xl shadow-xl flex items-center gap-3 bg-green-100 text-green-800 border border-green-200">
          <Check className="w-5 h-5" /><span className="font-medium">{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-2"><X className="w-4 h-4" /></button>
        </div>
      )}
      {error && (
        <div className="fixed top-4 right-4 z-50 p-4 rounded-xl shadow-xl flex items-center gap-3 bg-red-100 text-red-800 border border-red-200">
          <AlertCircle className="w-5 h-5" /><span className="font-medium">{error}</span>
          <button onClick={() => setError(null)} className="ml-2"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-700 p-6 rounded-2xl text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Eventi Totali</p>
              <p className="text-4xl font-bold mt-1">{events.length}</p>
              <p className="text-blue-200 text-xs mt-1">Salvati in MongoDB</p>
            </div>
            <Calendar className="w-8 h-8 text-blue-100" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-700 p-6 rounded-2xl text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Prenotazioni</p>
              <p className="text-4xl font-bold mt-1">{bookings.length}</p>
              <p className="text-green-2 00 text-xs mt-1">
                {bookings.filter(b => b.status === 'confirmed').length} confermate
              </p>
            </div>
            <Users className="w-8 h-8 text-green-100" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-yellow-700 p-6 rounded-2xl text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm font-medium">Posti Occupati</p>
              <p className="text-4xl font-bold mt-1">
                {events.reduce((sum, e) => sum + (e.bookedParticipants || 0), 0)}
              </p>
              <p className="text-yellow-200 text-xs mt-1">
                su {events.reduce((sum, e) => sum + e.maxParticipants, 0)} totali
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-yellow-100" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-700 p-6 rounded-2xl text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">In Attesa</p>
              <p className="text-4xl font-bold mt-1">
                {bookings.filter(b => b.status === 'pending').length}
              </p>
              <p className="text-purple-200 text-xs mt-1">Da confermare</p>
            </div>
            <Activity className="w-8 h-8 text-purple-100" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Eventi Attivi */}
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
          <h3 className="text-xl font-bold text-gray-800 mb-6">Eventi Attivi</h3>
          <div className="space-y-4">
            {events.slice(0, 5).map(e => (
              <div key={e._id} className="border border-gray-200 p-4 rounded-xl">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-800 mb-2">{e.title}</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(e.date)} alle {e.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{e.location}</span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${Math.min(((e.bookedParticipants || 0) / e.maxParticipants) * 100, 100)}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {e.bookedParticipants || 0}/{e.maxParticipants} posti
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {events.length === 0 && (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Nessun evento creato</p>
                <p className="text-sm text-gray-400 mt-1">Vai alla sezione Eventi per crearne uno</p>
              </div>
            )}
          </div>
        </div>

        {/* Prenotazioni Recenti */}
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
          <h3 className="text-xl font-bold text-gray-800 mb-6">Prenotazioni Recenti</h3>
          <div className="space-y-4">
            {bookings.slice(0, 5).map(bk => (
              <div key={bk._id} className="border border-gray-200 p-4 rounded-xl">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-800">{bk.guestName}</h4>
                    <p className="text-sm text-gray-600">{bk.guestEmail}</p>
                    <p className="text-xs text-gray-500">{bk.event?.title || 'Evento eliminato'}</p>
                    <div className="mt-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        bk.status === 'confirmed' ? 'bg-green-100 text-green-800'
                          : bk.status === 'cancelled' ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {bk.status === 'confirmed' ? 'Confermata' : bk.status === 'cancelled' ? 'Cancellata' : 'In Attesa'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    {bk.status === 'pending' && (
                      <button
                        onClick={() => updateBookingStatus(bk._id, 'confirmed')}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteBooking(bk._id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {bookings.length === 0 && (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Nessuna prenotazione</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==========================
   Admin Events (CRUD)
   ========================== */
const AdminEventsView = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '', date: '', time: '', duration: 60, location: '',
    maxParticipants: 10, description: '', collaborators: ''
  });

  const showSuccess = (m) => { setSuccess(m); setTimeout(() => setSuccess(null), 4000); };
  const showError = (m) => { setError(m); setTimeout(() => setError(null), 4000); };

  const resetEventForm = () => {
    setEventForm({ title: '', date: '', time: '', duration: 60, location: '', maxParticipants: 10, description: '', collaborators: '' });
    setEditingEvent(null);
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const r = await fetch(`${API_BASE}/events`);
      const data = await r.json();
      if (data.success) setEvents(data.data);
      else showError('Errore nel caricamento degli eventi');
    } catch {
      showError('Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!eventForm.title || !eventForm.date || !eventForm.time || !eventForm.location) {
      showError('Compila tutti i campi obbligatori'); return;
    }
    setLoading(true);
    try {
      const method = editingEvent ? 'PUT' : 'POST';
      const url = editingEvent ? `${API_BASE}/events/${editingEvent._id}` : `${API_BASE}/events`;
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...eventForm,
          collaborators: eventForm.collaborators
            ? eventForm.collaborators.split(',').map(e => e.trim()).filter(Boolean)
            : []
        })
      });
      const data = await r.json();
      if (data.success) {
        showSuccess(editingEvent ? 'Evento aggiornato con successo!' : 'Evento creato con successo!');
        setShowEventForm(false); resetEventForm(); fetchEvents();
      } else showError(data.message || 'Errore nel salvataggio');
    } catch {
      showError('Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ev) => {
    setEditingEvent(ev);
    setEventForm({
      title: ev.title,
      date: ev.date?.split('T')[0] || '',
      time: ev.time,
      duration: ev.duration,
      location: ev.location,
      maxParticipants: ev.maxParticipants,
      description: ev.description || '',
      collaborators: ev.collaborators ? ev.collaborators.join(', ') : ''
    });
    setShowEventForm(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo evento?')) return;
    try {
      setLoading(true);
      const r = await fetch(`${API_BASE}/events/${eventId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await r.json();
      if (data.success) { showSuccess('Evento eliminato con successo!'); fetchEvents(); }
      else showError(data.message || 'Errore nell\'eliminazione');
    } catch {
      showError('Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setEventForm({ ...eventForm, [e.target.name]: e.target.value });

  return (
    <div className="space-y-6">
      {success && (
        <div className="fixed top-4 right-4 z-50 p-4 rounded-xl shadow-xl flex items-center gap-3 bg-green-100 text-green-800 border border-green-200">
          <Check className="w-5 h-5" /><span className="font-medium">{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-2"><X className="w-4 h-4" /></button>
        </div>
      )}
      {error && (
        <div className="fixed top-4 right-4 z-50 p-4 rounded-2xl shadow-xl flex items-center gap-3 bg-red-100 text-red-800 border border-red-200">
          <AlertCircle className="w-5 h-5" /><span className="font-medium">{error}</span>
          <button onClick={() => setError(null)} className="ml-2"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Gestione Eventi</h2>
        <div className="flex gap-3">
          <button
            onClick={fetchEvents}
            className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
          </button>
          <button
            onClick={() => { resetEventForm(); setShowEventForm(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" /> Nuovo Evento
          </button>
        </div>
      </div>

      {loading && events.length === 0 ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full border-4 border-blue-600 border-t-transparent w-12 h-12"></div>
          <span className="ml-4 text-gray-600">Caricamento eventi...</span>
        </div>
      ) : (
        <div className="grid gap-4">
          {events.map(ev => (
            <div key={ev._id} className="bg-white p-6 rounded-xl shadow-lg border">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-800">{ev.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      (ev.bookedParticipants || 0) >= ev.maxParticipants ? 'bg-red-100 text-red-800'
                        : (ev.bookedParticipants || 0) > ev.maxParticipants * 0.7 ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {ev.bookedParticipants || 0}/{ev.maxParticipants} posti
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {new Date(ev.date).toLocaleDateString('it-IT')} alle {ev.time} ({ev.duration}min)
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {ev.location}
                    </div>
                    {ev.collaborators && ev.collaborators.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Collaboratori: {ev.collaborators.length}
                      </div>
                    )}
                  </div>

                  {ev.description && <p className="text-gray-600 mt-2">{ev.description}</p>}

                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          (ev.bookedParticipants || 0) >= ev.maxParticipants ? 'bg-red-500'
                            : (ev.bookedParticipants || 0) > ev.maxParticipants * 0.7 ? 'bg-yellow-500'
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(((ev.bookedParticipants || 0) / ev.maxParticipants) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(ev)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Modifica"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(ev._id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Elimina"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {events.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-500 mb-2">Nessun evento trovato</h3>
              <p className="text-gray-400 mb-6">Inizia creando il tuo primo evento</p>
              <button
                onClick={() => { resetEventForm(); setShowEventForm(true); }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                Crea Primo Evento
              </button>
            </div>
          )}
        </div>
      )}

      {/* Event Form Modal */}
      {showEventForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white p-8 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">
              {editingEvent ? 'Modifica Evento' : 'Crea Nuovo Evento'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text" name="title" placeholder="Titolo evento *" required
                  className="p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={eventForm.title} onChange={handleChange}
                />
                <input
                  type="date" name="date" required
                  className="p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={eventForm.date} onChange={handleChange}
                />
                <input
                  type="time" name="time" required
                  className="p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={eventForm.time} onChange={handleChange}
                />
                <input
                  type="number" name="duration" min="15" max="480" placeholder="Durata (minuti)"
                  className="p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={eventForm.duration} onChange={handleChange}
                />
                <input
                  type="text" name="location" placeholder="Luogo/Stand *" required
                  className="p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={eventForm.location} onChange={handleChange}
                />
                <input
                  type="number" name="maxParticipants" min="1" max="1000" placeholder="Partecipanti massimi"
                  className="p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={eventForm.maxParticipants} onChange={handleChange}
                />
              </div>

              <textarea
                name="description" rows="3" placeholder="Descrizione evento"
                className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={eventForm.description} onChange={handleChange}
              />

              <input
                type="text" name="collaborators" placeholder="Email collaboratori (separate da virgola)"
                className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={eventForm.collaborators} onChange={handleChange}
              />

              <div className="flex gap-4 mt-8">
                <button
                  type="submit" disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2"
                >
                  {loading ? <div className="animate-spin rounded-full border-2 border-white border-t-transparent w-5 h-5" /> : null}
                  {editingEvent ? 'Aggiorna Evento' : 'Crea Evento'}
                </button>
                <button
                  type="button" onClick={() => { setShowEventForm(false); resetEventForm(); }}
                  className="px-8 bg-gray-500 hover:bg-gray-600 text-white py-4 rounded-xl font-semibold"
                >
                  Annulla
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

/* ==========================
   App Admin/Collab Wrapper
   ========================== */
const MainApp = () => {
  const [currentView, setCurrentView] = useState('dashboard');

  const renderView = () => {
    switch (currentView) {
      case 'dashboard': return <AdminDashboard />;
      case 'events': return <AdminEventsView />;
      case 'bookings':
        return (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-500 mb-2">Gestione Prenotazioni</h3>
            <p className="text-gray-400">Vista gestione prenotazioni in sviluppo...</p>
          </div>
        );
      case 'profile':
        return <ProfileView />;
      default: return <AdminDashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <AuthenticatedHeader currentView={currentView} setCurrentView={setCurrentView} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{renderView()}</main>
    </div>
  );
};

/* ==========================
   Wrapper Autenticazione
   ========================== */
const AuthWrapper = ({ authMode, setAuthMode }) => {
  const { isAuthenticated, loading, isUser } = useAuth();
  const [resetToken, setResetToken] = useState(null);
  const [showResetSuccess, setShowResetSuccess] = useState(false);

  // Controlla token reset password nell'URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token && window.location.pathname === '/reset-password') {
      setResetToken(token);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full border-4 border-blue-600 border-t-transparent w-12 h-12 mx-auto mb-4" />
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Success dopo reset
  if (showResetSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Password Aggiornata</h2>
          <p className="text-gray-600 mb-6">
            La tua password è stata aggiornata con successo. Ora puoi accedere con la nuova password.
          </p>
          <button
            onClick={() => {
              setShowResetSuccess(false);
              setAuthMode('login');
              setResetToken(null);
              window.history.replaceState({}, document.title, '/');
            }}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold"
          >
            Vai al Login
          </button>
        </div>
      </div>
    );
  }

  // Flow reset password
  if (resetToken) {
    return (
      <ResetPasswordForm
        token={resetToken}
        onComplete={(success) => {
          if (success) {
            setShowResetSuccess(true);
            setResetToken(null);
            window.history.replaceState({}, document.title, '/');
          }
        }}
      />
    );
  }

  // Flow forgot password
  if (authMode === 'forgot-password') {
    return <ForgotPasswordForm onBack={() => setAuthMode('login')} />;
  }

  // Non autenticati: login/registrazione
  if (!isAuthenticated) {
    return authMode === 'login'
      ? (
        <LoginForm
          onSwitchToRegister={() => setAuthMode('register')}
          onForgotPassword={() => setAuthMode('forgot-password')}
        />
      ) : (
        <RegisterForm onSwitchToLogin={() => setAuthMode('login')} />
      );
  }

  // Utenti normali (ospiti)
  if (isUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <AuthenticatedHeader currentView="bookings" setCurrentView={() => {}} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <UserBookingsView />
        </main>
      </div>
    );
  }

  // Admin/Collaboratori
  return <MainApp />;
};

/* ==========================
   Root App
   ========================== */
const BookingApp = () => {
  const [authMode, setAuthMode] = useState('login');
  return (
    <AuthProvider>
      <AuthWrapper authMode={authMode} setAuthMode={setAuthMode} />
    </AuthProvider>
  );
};

export default BookingApp;
